import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { clearPosts, clearSelection, deletePosts, resetSplitState, searchPost, searchSplitPanel, toggleViewType } from "../../../features/post";
import { updateTab } from "../../../features/project";
import PostCard from "../../../components/post/PostCard";
import { Alert, Box, Button, Grid, IconButton, LinearProgress, Pagination, Paper, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography, FormControlLabel } from "@mui/material";
import Masonry from '@mui/lab/Masonry';
import { Delete, Edit, GridView, ViewList } from "@mui/icons-material";
import EditPostsDialog from "../../post/EditPostsDialog";
import { openDialog } from "../../../features/dialog";
import PostRow from "../../post/PostRow";
import { numberFormat } from "highcharts";
import PostSortingLabel from "../../post/PostSortingLabel";

const SINGLE_GRID_COLUMNS = 5;
const SPLIT_GRID_COLUMNS = Math.max(1, Math.floor(SINGLE_GRID_COLUMNS / 2));

function ProjectFeedTab({tab, project}){
    const dispatch = useDispatch();
    const viewType = useSelector(state => state.post.viewType);
    // Selected posts uuids
    const selectedPosts = useSelector(state => state.post.selected);
    // Are we selecting posts?
    const selecting = selectedPosts && selectedPosts.length > 0;

    const loading = useSelector(state => state.post.loading);
    const posts = useSelector(state => state.post.filteredList);
    const total = useSelector(state => state.post.filteredListTotal);
    const offset = useSelector(state => state.post.filteredListOffset) || 0;
    const limit = useSelector(state => state.post.filteredListLimit) || 100;

    const splitState = useSelector(state => state.post.split);
    const recentPanel = splitState.recent || {};
    const engagementPanel = splitState.engagement || {};

    const baseFilters = useMemo(() => (tab.config && tab.config.filters) || {}, [tab.config]);
    const viewMode = baseFilters.viewMode || (baseFilters.orderBy === "engagement" ? "engagement" : "date");
    const isSplit = viewMode === "split";
    const splitSwap = !!baseFilters.splitSwap;

    const totalPages = Math.ceil(total/limit);
    const currentPage = offset > 0 ? (offset/limit) + 1 : 1;
    const [page, setPage] = useState(currentPage);

    useEffect(() => {
        if(!isSplit){
            setPage(currentPage);
        }
    }, [currentPage, isSplit]);

    const handleViewTypeClick = () => {
        dispatch(toggleViewType());
    } 

    const singleLimit = limit || 100;

    const loadPage = page => {
        const orderBy = viewMode === "engagement" ? "engagement" : "post_timestamp";
        dispatch(searchPost({
            ...baseFilters,
            orderBy,
            offset: (page-1) * singleLimit,
            limit: singleLimit,
            project_id: project.id,
            tab_id:tab.id
        }));
    };

    const loadSplitPage = (panel, pageNumber) => {
        const targetPanel = panel === "recent" ? recentPanel : engagementPanel;
        const panelLimit = targetPanel.limit || 100;
        const panelOffset = (pageNumber - 1) * panelLimit;
        dispatch(searchSplitPanel({
            panel,
            baseFilters,
            filters: {
                ...baseFilters,
                orderBy: panel === "recent" ? "post_timestamp" : "engagement",
                offset: panelOffset,
                limit: panelLimit,
                project_id: project.id,
                tab_id: tab.id
            }
        }));
    };

    useEffect(() => {
        if(isSplit){
            dispatch(resetSplitState());
            loadSplitPage("recent", 1);
            loadSplitPage("engagement", 1);
        }else{
            loadPage(1);
        }
    }, [isSplit, baseFilters, viewMode, tab.id, project.id]);
    
    const handleClearSelection = () => {
        dispatch(clearSelection());
    }

    useEffect(() => {
        // loadPage(1);
        return () => {
            dispatch(clearPosts())
        }
    },[])
    useEffect(() => {
        handleClearSelection();
    },[project])

    const handleChangePage = (event, _page) => {
        setPage(_page);
        loadPage(_page);
    }

    const totalFormatted = numberFormat(total || 0,0,".",",");

    const isGrid = isSplit ? true : viewType !== "list";
    const showToggle = !isSplit && posts && posts.length > 0;

    const singlePaginationHeader = !isSplit && <Grid container justifyContent={"space-between"} alignItems="center">
        <Grid item display={"flex"} justifyContent="center" alignItems={"center"}>
            {showToggle && <Tooltip title={"Switch to " + (isGrid ? "list" : "grid") + " view"}>
                <IconButton onClick={handleViewTypeClick} size="small">
                    {isGrid ? <>
                        <ViewList size="small"/>
                    </> : <>
                        <GridView size="small"/>
                    </>}
                </IconButton>
            </Tooltip> }
            {posts && posts.length > 0 && <Typography variant="body2">
                Displaying posts from {offset+1} to {Math.min(offset+limit, total)} of <b>{totalFormatted}</b>
                &nbsp; 
                sorted by {<PostSortingLabel tab={tab} />}
            </Typography> }
        </Grid>
        <Grid item>
            {posts && totalPages > 1 &&  <Stack spacing={2}>
                <Pagination color="secondary" page={page} count={totalPages} onChange={handleChangePage}/>
            </Stack> }
        </Grid>
    </Grid>;

    const splitHeader = isSplit && <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
            <Typography variant="body2"><b>View mode:</b> <PostSortingLabel tab={tab} /></Typography>
        </Grid>
        <Grid item>
            <FormControlLabel
                control={<Switch size="small" checked={splitSwap} onChange={() => {
                    dispatch(updateTab({
                        id: tab.id,
                        filters: {
                            ...baseFilters,
                            viewMode: 'split',
                            splitSwap: !splitSwap
                        }
                    }));
                }} />}
                label="Swap panels"
            />
        </Grid>
    </Grid>;

    const renderSplitPanel = (panelKey, title, panelData) => {
        const panelLimit = panelData.limit || 100;
        const panelTotal = panelData.total || 0;
        const panelPage = panelData.offset > 0 ? (panelData.offset / panelLimit) + 1 : 1;
        const panelTotalPages = Math.ceil(panelTotal / panelLimit) || 1;
        const formattedTotal = numberFormat(panelTotal,0,".",",");
        const items = panelData.items || [];
        return <Paper elevation={0} sx={{p:1, maxHeight: '80vh', overflowY: 'auto'}}>
            <Grid container justifyContent="space-between" alignItems="center" sx={{mb:1}}>
                <Grid item>
                    <Typography variant="subtitle1" sx={{fontWeight: 700, fontSize: '1.2rem', color:'#5a7983'}}>{title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {items.length > 0 ? <>Displaying posts {panelData.offset + 1} - {Math.min(panelData.offset + panelLimit, panelTotal)} of <b>{formattedTotal}</b></> : "No posts loaded"}
                    </Typography>
                </Grid>
                <Grid item>
                    {panelTotalPages > 1 && <Stack spacing={1}>
                        <Pagination size="small" color="secondary" page={panelPage} count={panelTotalPages} onChange={(event, newPage) => loadSplitPage(panelKey, newPage)} />
                    </Stack>}
                </Grid>
            </Grid>
            {panelData.loading && <LinearProgress sx={{mt: 1, mb: 2}}/>}
            {items.length === 0 && !panelData.loading && <Paper elevation={2}>
                <Box sx={{height: 160}} display="flex" alignItems="center" justifyContent={"center"}>
                    <Typography variant="h6" color="primary.dark">No posts found</Typography>
                </Box>
            </Paper>}
            {items.length > 0 && <Masonry columns={SPLIT_GRID_COLUMNS} spacing={2} sx={{mt:2}}>
                {!panelData.loading && items.map(post => {
                    return <Box minHeight={220} key={post.uuid}><PostCard post={post} /></Box>
                })}
            </Masonry>}
            {panelTotalPages > 1 && <Box mt={2}>
                <Stack spacing={1}>
                    <Pagination size="small" color="secondary" page={panelPage} count={panelTotalPages} onChange={(event, newPage) => loadSplitPage(panelKey, newPage)} />
                </Stack>
            </Box>}
        </Paper>
    }

    return <Box mt={2}>
        <EditPostsDialog />

        {singlePaginationHeader}
        {splitHeader}

        {selecting && <Paper elevation={6} sx={{ width: '50%', left: "25%", bottom: 0, position: 'fixed'}}>
            <Alert variant="filled" color="primary" action={
                <Box gap={2} display="flex">
                    <Button color="secondary" size="small" onClick={() => dispatch(openDialog("editPosts"))}>
                        <Edit sx={{mr: 1}} fontSize="small"></Edit>
                        edit
                    </Button>
                    <Button color="secondary" size="small" onClick={() => dispatch(deletePosts())}>
                        <Delete sx={{mr: 1}} fontSize="small" />
                        trash
                    </Button>
                </Box>
            }>
                <span><b>{selectedPosts.length} posts</b> selected</span>
                <b onClick={handleClearSelection} style={{textDecoration: 'underline', cursor: 'pointer', marginLeft: 10}}>undo</b>
            </Alert>
        </Paper>}

        {!isSplit && <>
            {loading && <LinearProgress sx={{mt: 2, mb: 2}}/>}
            {posts && posts.length === 0 && !loading && <>
                <Paper elevation={2}>
                    <Box sx={{height: 200}} display="flex" alignItems="center" justifyContent={"center"}>
                        <Typography variant="h4" color="primary.dark">No posts found</Typography>
                    </Box>
                </Paper>
            </>}
            {isGrid && posts && posts.length > 0 && <Masonry columns={SINGLE_GRID_COLUMNS} spacing={2} sx={{mt:2}}>
                {!loading && posts.map(post => {
                    return <Box minHeight={220} key={post.uuid}><PostCard post={post} /></Box>
                })}
            </Masonry>}
            {!isGrid && posts && posts.length > 0 && <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell></TableCell>
                        <TableCell>Post</TableCell>
                        <TableCell>Platform</TableCell>
                        <TableCell>Priority</TableCell>
                        <TableCell>Sentiment</TableCell>
                        <TableCell>Tags</TableCell>
                        <TableCell>Language</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {posts.map((p, index) => <PostRow key={index} project={project} post={p} />)}
                </TableBody>
            </Table>}
            <Box mt={2}>
                {singlePaginationHeader}
            </Box>
        </>}

        {isSplit && <Grid container spacing={2} sx={{mt:2}}>
            <Grid item xs={12} md={6}>
                {renderSplitPanel(splitSwap ? "engagement" : "recent", splitSwap ? "Top engagement" : "Most recent", splitSwap ? engagementPanel : recentPanel)}
            </Grid>
            <Grid item xs={12} md={6}>
                {renderSplitPanel(splitSwap ? "recent" : "engagement", splitSwap ? "Most recent" : "Top engagement", splitSwap ? recentPanel : engagementPanel)}
            </Grid>
        </Grid>}
    </Box>
}

export default ProjectFeedTab;